import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion';
import Lenis from 'lenis';
import { Helmet } from 'react-helmet';

import './PortraitsSquare.scss'

import Header from '../components/Header.js';
import { getThumbnailUrl, isVideo, getVideoUrl, getThumbnailDimensions } from '../utils/imageLoader.js';

const Artworks = ({ artworks, buttons }) => {
    const navigate = useNavigate()

    useEffect(() => {
        document.title = "Artworks • Alex de Brabant";
    }, []);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Smooth/slow scroll only on Artworks page
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            smoothTouch: true,   // enable smoothing on touch
            wheelMultiplier: 0.8,
            touchMultiplier: 0.9,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // Handle window resize to recalculate text positions
    useEffect(() => {
        const handleResize = () => {
            // Recalculate all text positions after resize
            const images = document.querySelectorAll('.portrait-img-square');
            images.forEach((img, index) => {
                handleImageLoad({ target: img }, index);
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleArtworkClick = (artwork) => {
        navigate(`/${artwork.slug}`);
    }

    const handleImageLoad = (event, idx) => {
        const img = event.target;
        const container = img.closest('.portrait-img-container-square');
        const textElement = container.querySelector('.portrait-name-square');

        if (img && textElement) {
            // Calculate the actual image position within the flex container
            const imgWidth = img.offsetWidth;

            // Since the container uses flex with center alignment, calculate the offset
            /* const imageLeftOffset = (containerWidth - imgWidth) / 2; */

            // Set the text position to align with the image's left edge
            //textElement.style.marginLeft = `${imageLeftOffset / 2}px`;
            textElement.style.width = `${imgWidth}px`;
        }
    }

    return (

        <AnimatePresence mode="wait">
            <motion.div
                className="portraits-container-square"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
            >
                <Helmet>
                    <title>Artworks • Alex de Brabant</title>
                    <meta name="description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <link rel="canonical" href="https://alexdebrabant.com/artworks" />
                    <meta property="og:title" content="Artworks • Alex de Brabant" />
                    <meta property="og:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Artworks • Alex de Brabant" />
                    <meta name="twitter:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                </Helmet>
                <Header pageTitle="Artworks" buttons={buttons} />

                <div className="portraits-grid-square">
                    {artworks.map((artwork, idx) => (
                        <div
                            key={idx}
                            className="portrait-card-square"
                            onClick={() => handleArtworkClick(artwork)}
                        >
                            <div className="portrait-img-container-square">
                                {artwork.mainPicture ? (
                                    isVideo(artwork.mainPicture) ? (
                                        <video
                                            className="portrait-img-square"
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            onLoad={(e) => handleImageLoad(e, idx)}
                                        >
                                            <source src={getVideoUrl(artwork.mainPicture)} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : (
                                        <img
                                            src={getThumbnailUrl(artwork.mainPicture)}
                                            alt={artwork.title}
                                            loading="lazy"
                                            width={getThumbnailDimensions(artwork.mainPicture).width}
                                            height={getThumbnailDimensions(artwork.mainPicture).height}
                                            className="portrait-img-square"
                                            onLoad={(e) => handleImageLoad(e, idx)}
                                        />
                                    )
                                ) : (
                                    <div className="portrait-img-placeholder">
                                        <span>no image available</span>
                                    </div>
                                )}
                                <div className="portrait-name-square">{artwork.title}</div>
                            </div>

                        </div>
                    ))}
                </div>
            </motion.div>
        </AnimatePresence>
    )
}

export default Artworks